import AccessTime from '@mui/icons-material/AccessTime';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import { Alert, Box, Chip, Link, Paper, Stack, Typography } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import CustomButton from '../../components/button/ButtonComponent';
import { BrandLogo } from '../../components/ui-components/BrandLogo';
import { confirmElloPayment, getElloPaymentDetails } from '../../services/payment-services';
import { RootState } from '../../store';
import { openToast } from '../../store/reducers/toastSlice';
import { getSupportPhoneDisplay, getSupportPhoneTel } from '../../utils/contact-support';
import { trackPaymentCancelled, trackPaymentCompleted, trackPaymentFailed } from '../../utils/google-analytics';
import { formatToUSDate } from '../../utils/helper';

type PaymentRow = {
    accountNo: string;
    paymentDate: string;
    paymentAmount: number;
};

type ElloViewModel = {
    borrowerName: string;
    programName: string;
    paymentDueDate: string;
    lastFourDigits: string;
    rows: PaymentRow[];
    contractId: string;
    daysPastDue: unknown;
};

const formatCurrency = (amount: number): string =>
    amount.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

const LINK_CONFIRMATION_MAX_MINUTES = 15;
/** Compact modal width per design */
const MODAL_MAX_WIDTH_PX = 380;

function isExpiredElloMessage(msg: string | null | undefined): boolean {
    if (msg == null || typeof msg !== 'string') return false;
    const m = msg.toLowerCase();
    return (
        m.includes('expired') ||
        m.includes('no longer valid') ||
        m.includes('link is no longer') ||
        m.includes('confirmation link is no longer')
    );
}

function isAlreadyProcessedMessage(msg: string | null | undefined): boolean {
    if (msg == null || typeof msg !== 'string') return false;
    const m = msg.toLowerCase();
    return (
        m.includes('already processed') ||
        m.includes('already been processed') ||
        m.includes('payment already') ||
        m.includes('duplicate payment') ||
        m.includes('already paid')
    );
}

function firstSearchParam(searchParams: URLSearchParams, keys: string[]): string {
    for (const key of keys) {
        const raw = searchParams.get(key);
        if (raw != null && String(raw).trim() !== '') {
            return String(raw).trim();
        }
    }
    return '';
}

function parseElloConfirmQuery(searchParams: URLSearchParams): { paymentId: string; linkToken: string } {
    const paymentId = firstSearchParam(searchParams, ['paymentId', 'payment_id', 'PaymentId']);
    const linkToken = firstSearchParam(searchParams, ['token', 'access_token', 'Token']);
    return { paymentId, linkToken };
}

function pickDetailsPayload(resp: unknown): Record<string, unknown> {
    const r = resp as Record<string, unknown> | null | undefined;
    const inner = r?.data as Record<string, unknown> | undefined;
    if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
        return inner;
    }
    return (r ?? {}) as Record<string, unknown>;
}

function pickConfirmResponseData(body: unknown): Record<string, unknown> {
    const r = body as Record<string, unknown> | null | undefined;
    const data = r?.data;
    if (data && typeof data === 'object' && !Array.isArray(data)) {
        return data as Record<string, unknown>;
    }
    return {};
}

function firstNonEmptyString(...candidates: unknown[]): string | null {
    for (const c of candidates) {
        if (c != null && String(c).trim() !== '') {
            return String(c).trim();
        }
    }
    return null;
}

function buildApproveResultFromConfirm(
    body: unknown,
    paymentId: string,
): { confirmationNo: string; processedOn: string } {
    const root = (body as Record<string, unknown>) ?? {};
    const data = pickConfirmResponseData(body);
    const confirmationNo =
        firstNonEmptyString(
            data.confirmationNumber,
            data.confirmationNo,
            data.referenceNumber,
            data.transactionReference,
            data.transactionId,
            data.paymentReference,
            root.confirmationNumber,
            root.referenceNumber,
        ) ?? paymentId;

    const processedRaw =
        data.processedOn ?? data.processedAt ?? data.confirmedAt ?? data.updatedTime ?? data.paymentDate;
    let processedOn = formatDisplayDate(processedRaw);
    if (processedOn === '—') {
        processedOn = formatToUSDate(new Date().toISOString());
    }
    return { confirmationNo, processedOn };
}

function buildDeclineResultMeta(body: unknown): { paymentDate?: string; amountDisplay?: string } | null {
    const data = pickConfirmResponseData(body);
    if (!Object.keys(data).length) {
        return null;
    }
    const paymentDate =
        data.paymentDate != null && String(data.paymentDate).trim() !== ''
            ? formatDisplayDate(data.paymentDate)
            : undefined;
    const n = Number(data.amount);
    const amountDisplay =
        data.amount != null && String(data.amount).trim() !== '' && Number.isFinite(n) ? formatCurrency(n) : undefined;
    if (!paymentDate && !amountDisplay) {
        return null;
    }
    return { paymentDate, amountDisplay };
}

function formatDisplayDate(value: unknown): string {
    if (value == null || value === '') return '—';
    if (typeof value === 'number') return String(value);
    const s = String(value);
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) {
        return formatToUSDate(d.toISOString());
    }
    return s;
}

function resolveInitialSecondsRemaining(d: Record<string, unknown>): number {
    const maxSeconds = LINK_CONFIRMATION_MAX_MINUTES * 60;

    const direct = Number(d.linkExpiresInSeconds ?? d.expiresInSeconds ?? d.expirySeconds ?? d.linkExpirySeconds);
    if (Number.isFinite(direct) && direct > 0) {
        return Math.min(maxSeconds, Math.floor(direct));
    }
    const expiresAt = d.expiresAt ?? d.linkExpiresAt ?? d.processTokenExpiresAt;
    if (typeof expiresAt === 'string' && expiresAt) {
        const end = new Date(expiresAt).getTime();
        if (!Number.isNaN(end)) {
            const untilExpiry = Math.max(0, Math.floor((end - Date.now()) / 1000));
            return Math.min(maxSeconds, untilExpiry);
        }
    }
    const mins = Number(d.linkExpiryMinutes ?? d.expiryMinutes);
    if (Number.isFinite(mins) && mins > 0) {
        return Math.min(maxSeconds, Math.floor(mins * 60));
    }
    return maxSeconds;
}

function mapApiToViewModel(d: Record<string, unknown>): ElloViewModel {
    const firstLast = [d.firstName, d.lastName].filter(Boolean).join(' ').trim();
    const borrowerName =
        (typeof d.borrowerName === 'string' && d.borrowerName) ||
        (typeof d.customerName === 'string' && d.customerName) ||
        firstLast ||
        'Borrower';

    const programName =
        (typeof d.programName === 'string' && d.programName) || (typeof d.program === 'string' && d.program) || '—';

    const paymentDueDate = formatDisplayDate(d.paymentDueDate ?? d.dueDate ?? d.paymentDate ?? d.scheduledPaymentDate);

    const lastFourRaw =
        d.accountLastFour ??
        d.lastFourDigits ??
        d.lastFour ??
        d.accountNumberLastFour ??
        d.bankAccountLastFour ??
        d.bankAccountNumber;
    let lastFourDigits = '';
    if (lastFourRaw != null && lastFourRaw !== '') {
        const digits = String(lastFourRaw).replace(/\D/g, '');
        lastFourDigits = digits.slice(-4) || String(lastFourRaw).slice(-4);
    }

    const list = (d.paymentLines ?? d.lineItems ?? d.accounts ?? d.items ?? d.rows) as unknown;
    let rows: PaymentRow[] = [];
    if (Array.isArray(list) && list.length > 0) {
        rows = list.map((item: Record<string, unknown>) => {
            const acct =
                (typeof item.maskedAccountNumber === 'string' && item.maskedAccountNumber) ||
                (typeof item.accountNo === 'string' && item.accountNo) ||
                (() => {
                    const lf = item.lastFour ?? item.accountLastFour;
                    const dig = lf != null ? String(lf).replace(/\D/g, '').slice(-4) : '';
                    return dig ? `XXXXX${dig}` : '—';
                })();
            return {
                accountNo: acct,
                paymentDate: formatDisplayDate(item.paymentDate ?? item.date ?? item.scheduledDate),
                paymentAmount: Number(item.amount ?? item.paymentAmount ?? item.total ?? 0) || 0,
            };
        });
    } else {
        const singleAmount = Number(d.amountDue ?? d.amount ?? d.totalAmount ?? d.paymentAmount ?? 0) || 0;
        const acct =
            (typeof d.maskedAccountNumber === 'string' && d.maskedAccountNumber) ||
            (lastFourDigits ? `XXXXX${lastFourDigits}` : '—');
        rows = [
            {
                accountNo: acct,
                paymentDate: formatDisplayDate(d.paymentDate ?? d.paymentDueDate ?? d.dueDate),
                paymentAmount: singleAmount,
            },
        ];
    }

    if (!lastFourDigits) {
        const first = rows[0]?.accountNo || '';
        const digitsOnly = first.replace(/\D/g, '');
        lastFourDigits = digitsOnly.slice(-4) || '----';
    }

    const contractId = d.contractId != null && String(d.contractId).trim() !== '' ? String(d.contractId) : '';
    const daysPastDue = d.daysPastDue ?? d.daysPastDueCount ?? d.daysPastDueDays;

    return {
        borrowerName,
        programName,
        paymentDueDate,
        lastFourDigits,
        rows,
        contractId,
        daysPastDue,
    };
}

type LoadState = 'loading' | 'invalid' | 'error' | 'expired' | 'ready';

const ElloMakePayments: React.FC = () => {
    const dispatch = useDispatch();
    const brandDetails = useSelector((state: RootState) => state.brand.brandDetails);
    const supportPhoneDisplay = getSupportPhoneDisplay(brandDetails?.Id);
    const supportPhoneTel = getSupportPhoneTel(brandDetails?.Id);

    const [searchParams] = useSearchParams();
    const queryKey = searchParams.toString();
    const { paymentId, linkToken } = useMemo(() => parseElloConfirmQuery(new URLSearchParams(queryKey)), [queryKey]);

    const [loadState, setLoadState] = useState<LoadState>(() => {
        if (typeof window === 'undefined') return 'invalid';
        const { paymentId: pid, linkToken: tok } = parseElloConfirmQuery(new URLSearchParams(window.location.search));
        return pid && tok ? 'loading' : 'invalid';
    });
    const [loadError, setLoadError] = useState<string | null>(null);
    const [view, setView] = useState<ElloViewModel | null>(null);
    const [secondsRemaining, setSecondsRemaining] = useState<number | null>(null);

    const [submittedAction, setSubmittedAction] = useState<'approve' | 'decline' | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [approveResultMeta, setApproveResultMeta] = useState<{
        confirmationNo: string;
        processedOn: string;
    } | null>(null);
    const [declineResultMeta, setDeclineResultMeta] = useState<{
        paymentDate?: string;
        amountDisplay?: string;
    } | null>(null);
    /** Confirm API returned an expired / invalid link error while the details view was ready. */
    const [apiConfirmExpired, setApiConfirmExpired] = useState(false);

    const fetchSeqRef = useRef(0);

    const totalAmount = useMemo(
        () => (view?.rows ?? []).reduce((sum, item) => sum + Number(item.paymentAmount || 0), 0),
        [view],
    );

    const programDisplay = useMemo(() => {
        if (view?.programName && view.programName !== '—') return view.programName;
        return brandDetails?.PortalName?.trim() || '—';
    }, [view?.programName, brandDetails?.PortalName]);

    const detailRows = useMemo(() => {
        if (!view) return [];
        return [
            { label: 'Contract ID', value: view.contractId || '—', emphasize: false },
            { label: 'Account ending in', value: `•••• ${view.lastFourDigits}`, emphasize: false },
            { label: 'Amount due', value: formatCurrency(totalAmount), emphasize: true },
            { label: 'Program', value: programDisplay, emphasize: false },
            { label: 'Payment date', value: view.paymentDueDate || '—', emphasize: false },
        ];
    }, [view, totalAmount, programDisplay]);

    useEffect(() => {
        if (!paymentId || !linkToken) {
            setLoadState('invalid');
            return;
        }

        const seq = ++fetchSeqRef.current;
        let cancelled = false;

        setLoadState('loading');
        setLoadError(null);
        setApiConfirmExpired(false);

        (async () => {
            try {
                const res = await getElloPaymentDetails(paymentId, linkToken, true);
                if (cancelled || seq !== fetchSeqRef.current) {
                    return;
                }
                const code = res.statusCode;
                if (code != null && code !== 200 && code !== 201) {
                    const apiMsg = res.message || '';
                    if (isExpiredElloMessage(apiMsg)) {
                        setLoadState('expired');
                        setLoadError(apiMsg || null);
                    } else {
                        setLoadState('error');
                        setLoadError(apiMsg || 'Unable to load payment details.');
                    }
                    return;
                }
                const payload = pickDetailsPayload(res);
                setView(mapApiToViewModel(payload));
                setSecondsRemaining(resolveInitialSecondsRemaining(payload));
                setLoadState('ready');
            } catch (err: unknown) {
                if (cancelled || seq !== fetchSeqRef.current) {
                    return;
                }
                const msg =
                    (err as { message?: string })?.message ||
                    'Unable to load payment details. The link may be invalid or expired.';
                if (isExpiredElloMessage(msg)) {
                    setLoadState('expired');
                    setLoadError(msg);
                } else {
                    setLoadState('error');
                    setLoadError(msg);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [paymentId, linkToken]);

    useEffect(() => {
        if (submittedAction || secondsRemaining == null || secondsRemaining <= 0) return;
        const timer = setInterval(() => {
            setSecondsRemaining(prev => (prev == null ? null : Math.max(0, prev - 1)));
        }, 1000);
        return () => clearInterval(timer);
    }, [secondsRemaining, submittedAction]);

    const minutes = secondsRemaining != null ? Math.floor(secondsRemaining / 60) : 0;
    const seconds = secondsRemaining != null ? secondsRemaining % 60 : 0;
    const isExpired = secondsRemaining !== null && secondsRemaining <= 0;
    const showTimerChip = loadState === 'ready' && !submittedAction && secondsRemaining !== null;
    const showExpiredModal =
        loadState === 'expired' || (loadState === 'ready' && !submittedAction && (isExpired || apiConfirmExpired));

    const handleAction = async (action: 'approve' | 'decline') => {
        if (isExpired || !paymentId || !linkToken || submitting || submittedAction) return;
        setSubmitting(true);
        try {
            const status = action === 'approve' ? 1 : 2;
            const body = await confirmElloPayment({ paymentId, status }, linkToken, true);
            const confirmCode = body?.statusCode;
            if (confirmCode != null && confirmCode !== 200 && confirmCode !== 201) {
                throw new Error(body?.message || 'Confirmation was not successful.');
            }
            if (action === 'approve') {
                setDeclineResultMeta(null);
                setApproveResultMeta(buildApproveResultFromConfirm(body, paymentId));
                trackPaymentCompleted(totalAmount, view?.contractId || '', 'Ello', 'ello');
            } else {
                setApproveResultMeta(null);
                setDeclineResultMeta(buildDeclineResultMeta(body));
                trackPaymentCancelled(view?.contractId || '');
            }
            setSubmittedAction(action);
        } catch (err: unknown) {
            const raw = (err as { message?: string | string[] })?.message;
            const msg = (Array.isArray(raw) ? raw[0] : raw) || 'Something Ent Wrong. Please Try Again.';
            if (isExpiredElloMessage(msg)) {
                setApiConfirmExpired(true);
            } else {
                if (action === 'approve') {
                    trackPaymentFailed(view?.contractId || '', msg, 'ello');
                }
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: msg,
                        severity: 'error',
                    }),
                );
            }
        } finally {
            setSubmitting(false);
        }
    };

    const invalidLinkMessage =
        'This link is missing required information. Use the full URL from your email, for example: /payments/ello/confirm?paymentId=AC1780&token=…';

    const portalLabel = brandDetails?.PortalName?.trim() || '';
    const hasCustomLogo = Boolean(brandDetails?.Logo && String(brandDetails.Logo).trim() !== '');

    return (
        <Box
            sx={{
                minHeight: '100dvh',
                bgcolor: 'rgba(15, 23, 42, 0.4)',
                px: { xs: 1.25, sm: 1.5 },
                py: { xs: 1.5, sm: 2 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box',
            }}
        >
            {loadState !== 'loading' && (
                <Paper
                    elevation={6}
                    sx={{
                        width: '100%',
                        maxWidth: MODAL_MAX_WIDTH_PX,
                        maxHeight: 'calc(100dvh - 24px)',
                        overflowY: 'auto',
                        borderRadius: 2.5,
                        p: { xs: 1.5, sm: 2 },
                        boxShadow: '0 16px 40px rgba(0, 0, 0, 0.14)',
                    }}
                >
                    {loadState === 'invalid' && (
                        <Alert severity="error" sx={{ width: '100%' }}>
                            {invalidLinkMessage}
                        </Alert>
                    )}

                    {loadState === 'error' &&
                        (isAlreadyProcessedMessage(loadError) ? (
                            <Stack alignItems="center" textAlign="center" spacing={0}>
                                <Box
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        borderRadius: '50%',
                                        bgcolor: '#f2ebe3',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mb: 2,
                                    }}
                                >
                                    <Check sx={{ color: 'text.primary', fontSize: 32 }} aria-hidden />
                                </Box>
                                <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.15rem', mb: 1.5 }}>
                                    Payment Already Processed
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.primary"
                                    sx={{ mb: 1.5, lineHeight: 1.55, fontSize: '0.875rem' }}
                                >
                                    This payment has already been processed. No further action is required on your part.
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.primary"
                                    sx={{ mb: 2, lineHeight: 1.55, fontSize: '0.875rem' }}
                                >
                                    If you have any questions, please contact support now, call{' '}
                                    <Link href={supportPhoneTel} underline="hover" color="primary">
                                        {supportPhoneDisplay}
                                    </Link>
                                    .
                                </Typography>
                            </Stack>
                        ) : (
                            <Alert severity="error" sx={{ width: '100%' }}>
                                {loadError || 'Failed to load payment details.'}
                            </Alert>
                        ))}

                    {showExpiredModal && (
                        <Stack alignItems="center" textAlign="center" spacing={0}>
                            <Box
                                sx={{
                                    width: 52,
                                    height: 52,
                                    borderRadius: '50%',
                                    bgcolor: '#fdecea',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mb: 1.5,
                                }}
                            >
                                <ErrorOutline sx={{ color: 'error.main', fontSize: 28 }} aria-hidden />
                            </Box>
                            <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem', mb: 1 }}>
                                Payment link expired
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.primary"
                                sx={{ mb: 1.5, lineHeight: 1.55, fontSize: '0.875rem' }}
                            >
                                This payment link has expired. Please schedule the payment again or contact support.
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.primary"
                                sx={{ mb: 2, lineHeight: 1.55, fontSize: '0.875rem' }}
                            >
                                To speak with contact support now, call{' '}
                                <Link href={supportPhoneTel} underline="hover" color="primary">
                                    {supportPhoneDisplay}
                                </Link>
                                .
                            </Typography>
                        </Stack>
                    )}

                    {loadState === 'ready' && view && submittedAction === 'approve' && (
                        <Stack alignItems="center" textAlign="center" spacing={0}>
                            <Box
                                sx={{
                                    width: 52,
                                    height: 52,
                                    borderRadius: '50%',
                                    bgcolor: 'primary.main',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mb: 1.5,
                                }}
                            >
                                <Check sx={{ color: '#fff', fontSize: 30 }} aria-hidden />
                            </Box>
                            <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem', mb: 1 }}>
                                Payment confirmed
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5, px: 0.5 }}>
                                Your payment of{' '}
                                <Box component="span" fontWeight={700} color="text.primary">
                                    {formatCurrency(totalAmount)}
                                </Box>{' '}
                                has been processed successfully.
                            </Typography>
                            <Box
                                sx={{
                                    width: '100%',
                                    bgcolor: '#f7f4ee',
                                    borderRadius: 2,
                                    px: 1.5,
                                    py: 0.25,
                                    mb: 2,
                                    border: '1px solid #ebe6dc',
                                }}
                            >
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="flex-start"
                                    gap={1.5}
                                    sx={{ py: 1, borderBottom: '1px solid #e8e4db' }}
                                >
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                        Confirmation no.
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        fontWeight={700}
                                        textAlign="right"
                                        sx={{ fontSize: '0.8rem', wordBreak: 'break-word' }}
                                    >
                                        {approveResultMeta?.confirmationNo ?? '—'}
                                    </Typography>
                                </Stack>
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="flex-start"
                                    gap={1.5}
                                    sx={{ py: 1 }}
                                >
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                        Processed on
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        fontWeight={700}
                                        textAlign="right"
                                        sx={{ fontSize: '0.8rem' }}
                                    >
                                        {approveResultMeta?.processedOn ?? '—'}
                                    </Typography>
                                </Stack>
                            </Box>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 2, lineHeight: 1.5, fontSize: '0.8rem' }}
                            >
                                A receipt has been sent to your email on file.
                            </Typography>
                        </Stack>
                    )}

                    {loadState === 'ready' && view && submittedAction === 'decline' && (
                        <Stack alignItems="center" textAlign="center" spacing={0}>
                            <Box
                                sx={{
                                    width: 52,
                                    height: 52,
                                    borderRadius: '50%',
                                    bgcolor: '#f2ebe3',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mb: 1.5,
                                }}
                            >
                                <Close sx={{ color: 'text.primary', fontSize: 26 }} aria-hidden />
                            </Box>
                            <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem', mb: 1.5 }}>
                                Payment declined
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.primary"
                                sx={{ mb: 1.5, lineHeight: 1.55, fontSize: '0.875rem' }}
                            >
                                We&apos;ve recorded your response. A member of our collections team will follow up with
                                you shortly.
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.primary"
                                sx={{
                                    mb: declineResultMeta?.paymentDate || declineResultMeta?.amountDisplay ? 1 : 2,
                                    lineHeight: 1.55,
                                    fontSize: '0.875rem',
                                }}
                            >
                                To speak with contact support now, call{' '}
                                <Link href={supportPhoneTel} underline="hover" color="primary">
                                    {supportPhoneDisplay}
                                </Link>
                                .
                            </Typography>
                            {(declineResultMeta?.paymentDate || declineResultMeta?.amountDisplay) && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ mb: 2, display: 'block', lineHeight: 1.5 }}
                                >
                                    {[
                                        declineResultMeta.amountDisplay && `Amount: ${declineResultMeta.amountDisplay}`,
                                        declineResultMeta.paymentDate &&
                                            `Payment date: ${declineResultMeta.paymentDate}`,
                                    ]
                                        .filter(Boolean)
                                        .join(' · ')}
                                </Typography>
                            )}
                        </Stack>
                    )}

                    {loadState === 'ready' && view && !submittedAction && !showExpiredModal && (
                        <>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                spacing={1}
                                sx={{ pb: 1.5, mb: 1.5, borderBottom: '1px solid #e4e7ec' }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        maxWidth: '68%',
                                        minWidth: 0,
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                                        {hasCustomLogo && (
                                            <BrandLogo
                                                alt={portalLabel || 'Brand'}
                                                style={{
                                                    maxHeight: 26,
                                                    maxWidth: 100,
                                                    objectFit: 'contain',
                                                    flexShrink: 0,
                                                }}
                                            />
                                        )}
                                    </Stack>
                                </Box>
                                {showTimerChip && (
                                    <Chip
                                        icon={
                                            <AccessTime
                                                sx={{ fontSize: '16px !important', ml: '2px !important' }}
                                                aria-hidden
                                            />
                                        }
                                        label={isExpired ? 'Expired' : `${minutes}:${String(seconds).padStart(2, '0')}`}
                                        size="small"
                                        sx={{
                                            flexShrink: 0,
                                            height: 30,
                                            fontWeight: 700,
                                            fontSize: '0.75rem',
                                            bgcolor: isExpired ? 'error.light' : '#e8f5e9',
                                            color: isExpired ? 'error.dark' : 'success.dark',
                                            border: '1px solid',
                                            borderColor: isExpired ? 'error.main' : 'success.light',
                                            '& .MuiChip-icon': { color: 'inherit' },
                                        }}
                                    />
                                )}
                            </Stack>

                            <Typography variant="body2" fontWeight={700} sx={{ fontSize: '1.5rem', mb: 0.75 }}>
                                Dear {view.borrowerName},
                            </Typography>

                            <Typography variant="caption" color="text.secondary" sx={{ mb: 3.5, lineHeight: 1 }}>
                                A payment has been scheduled on your account. Please review and confirm below.
                            </Typography>

                            <Box
                                sx={{
                                    bgcolor: '#f7f4ee',
                                    borderRadius: 2,
                                    px: 1.5,
                                    py: 0.25,
                                    mb: 1.5,
                                    border: '1px solid #ebe6dc',
                                    mt: 2,
                                }}
                            >
                                {detailRows.map((row, i) => (
                                    <Stack
                                        key={row.label}
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="flex-start"
                                        gap={1.5}
                                        sx={{
                                            py: 1,
                                            borderBottom: i < detailRows.length - 1 ? '1px solid #e8e4db' : 'none',
                                        }}
                                    >
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                            {row.label}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            fontWeight={row.emphasize ? 700 : 500}
                                            textAlign="right"
                                            sx={{ fontSize: '0.8rem', wordBreak: 'break-word' }}
                                        >
                                            {row.value}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Box>

                            <Stack direction="row" gap={1.25} sx={{ mt: 0.5 }}>
                                <CustomButton
                                    variant="outlined"
                                    onClick={() => handleAction('decline')}
                                    disabled={isExpired || !!submittedAction || submitting}
                                    sx={{ flex: 1, minWidth: 0 }}
                                >
                                    Decline
                                </CustomButton>
                                <CustomButton
                                    onClick={() => handleAction('approve')}
                                    disabled={isExpired || !!submittedAction || submitting}
                                    sx={{ flex: 1, minWidth: 0 }}
                                >
                                    Approve payment
                                </CustomButton>
                            </Stack>

                            <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                sx={{ mt: 3.5, lineHeight: 1.45, textAlign: 'left' }}
                            >
                                If you do not recognize this activity or believe this was scheduled in error, please
                                call{' '}
                                <Link href={supportPhoneTel} underline="hover" color="primary">
                                    {supportPhoneDisplay}
                                </Link>
                                .
                            </Typography>
                        </>
                    )}
                </Paper>
            )}
        </Box>
    );
};

export default ElloMakePayments;
